import { useContext, useEffect } from 'react'
import { useStore } from 'zustand'

import { useAsyncError } from '@/modules/errors'
import { DocumentContext } from '../components'
import { type DocumentStore, useDocumentsStore } from '../stores'

const useDocumentStore = <T>(selector: (state: DocumentStore) => T): T => {
  const stores = useContext(DocumentContext)
  const store = stores?.documentStore

  if (!store) {
    throw new Error('useDocumentStore must be used within DocumentProvider')
  }

  return useStore(store, selector)
}

export const useLoadingDocument = () =>
  useDocumentsStore((state) => state.loadingDocuments)

export const useDocumentMutating = () =>
  useDocumentsStore((state) => state.mutating)

export const useDocument = () => useDocumentStore((state) => state.document)

export const useDocumentActions = () =>
  useDocumentStore((state) => state.actions)

export const useDocumentMutations = () =>
  useDocumentStore((state) => state.mutations)

export const useDocumentLoaders = () =>
  useDocumentStore((state) => state.loaders)

export const useDocumentPages = () => useDocumentStore((state) => state.pages)

export const useCurrentPageIndex = () =>
  useDocumentStore((state) => state.currentPage)

export const useCurrentPage = () =>
  useDocumentStore((state) => state.pages[state.currentPage] ?? null)

export const useCurrentBlockIndex = () =>
  useDocumentStore((state) => state.currentBlock)

export const useFetchDocument = (id: string) => {
  const loaders = useDocumentLoaders()
  const throwError = useAsyncError()

  useEffect(() => {
    loaders.fetchDocument(id).catch(throwError)
  }, [])
}
